import { ChevronLeftIcon, ChevronRightIcon, CopyIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Checkbox,
    Heading,
    HStack,
    IconButton,
    Input,
    Select,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useToast
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import AsyncSelect from 'react-select/async';

import {
    copyWeekSchedule,
    fetchAllRoutes,
    fetchCouriersBySearch,
    getCourierSchedule,
    saveWeekSchedule
} from '../../api';

/* ---------- helpers ---------- */

const formatDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const getWeekFromDate = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const monday = new Date(d);
    monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));

    return Array.from({ length: 7 }).map((_, i) => {
        const cur = new Date(monday);
        cur.setDate(monday.getDate() + i);
        return {
            date: formatDate(cur),
            startTime: '',
            routeId: '',
            dayOff: false
        };
    });
};

/* ---------- page ---------- */

const CreateUpdateSchedulePage = () => {
    const toast = useToast();

    const [courier, setCourier] = useState(null);
    const [couriers, setCouriers] = useState([]);
    const [routes, setRoutes] = useState([]);

    const [weekStart, setWeekStart] = useState(() => {
        const base = new Date();
        const monday = new Date(base);
        const day = monday.getDay();
        monday.setDate(monday.getDate() - (day === 0 ? 6 : day - 1));
        return monday;
    });

    const [week, setWeek] = useState([]);
    const [loading, setLoading] = useState(false);

    /* load couriers + routes */
    useEffect(() => {
        fetchCouriersBySearch().then(setCouriers);
        fetchAllRoutes(1, 999999).then(res => {
            if (res.success) setRoutes(res.data.routes);
        });
    }, []);

    const loadCourierOptions = (input, cb) => {
        cb(
            couriers.filter(c =>
                c.label.toLowerCase().includes(input.toLowerCase())
            )
        );
    };

    /* load week from backend */
    const loadWeek = async (courierId, baseDate) => {
        setLoading(true);
        const baseWeek = getWeekFromDate(baseDate);
        const start = baseWeek[0].date;
        const end = baseWeek[6].date;

        const res = await getCourierSchedule(courierId, { start, end });
        const existing = res.success ? res.data : [];

        const merged = baseWeek.map(d => {
            const found = existing.find(e => e.date === d.date);
            return found
                ? {
                    date: d.date,
                    startTime: found.startTime || '',
                    routeId: found.routeId?._id || '',
                    dayOff: found.dayOff
                }
                : d;
        });

        setWeek(merged);
        setLoading(false);
    };

    const changeWeek = (dir) => {
        const next = new Date(weekStart);
        next.setDate(next.getDate() + dir * 7);
        setWeekStart(next);
        if (courier) loadWeek(courier.value, next);
    };

    const updateDay = (i, field, value) => {
        const copy = [...week];
        copy[i][field] = value;
        setWeek(copy);
    };

    /* ---------- validation ---------- */
    const validateWeek = () => {
        for (const d of week) {
            if (!d.dayOff) {
                if (!d.startTime || !d.routeId) {
                    toast({
                        status: 'error',
                        title: 'Validation error',
                        description: `Missing start time or route for ${d.date}`
                    });
                    return false;
                }
            }
        }
        return true;
    };

    const saveWeek = async () => {
        if (!validateWeek()) return;

        await saveWeekSchedule({
            courierId: courier.value,
            week
        });

        toast({
            status: 'success',
            title: 'Week saved'
        });
    };

    const copyLastWeek = async () => {
        const lastWeekStart = new Date(weekStart.getTime() - 7 * 86400000);
        const fromStart = formatDate(lastWeekStart);
        const fromEnd = formatDate(new Date(lastWeekStart.getTime() + 6 * 86400000));
        const toStart = formatDate(weekStart);


        console.log('🕐 Copying from', fromStart, 'to', fromEnd);


        const res = await copyWeekSchedule({
            courierId: courier.value,
            fromStart,
            fromEnd,
            toStart
        });

        if (res.success && Array.isArray(res.data)) {
            setWeek(prev =>
                prev.map(day => {
                    const found = res.data.find(d => d.date === day.date);
                    return found ? { ...day, ...found } : day;
                })
            );

            toast({
                status: 'success',
                title: 'Last week copied'
            });
        }
    };


    return (
        <Box p={6}>
            <Heading mb={4}>Create / Update Schedule</Heading>

            <AsyncSelect
                cacheOptions
                loadOptions={loadCourierOptions}
                defaultOptions={couriers}
                value={courier}
                onChange={(c) => {
                    setCourier(c);
                    if (c) loadWeek(c.value, weekStart);
                }}
                placeholder="Search and select courier"
                isClearable
            />

            {courier && (
                <HStack mt={4} spacing={3}>
                    <IconButton
                        icon={<ChevronLeftIcon />}
                        onClick={() => changeWeek(-1)}
                        aria-label="Prev week"
                    />
                    <Text fontWeight="bold">
                        Week of {getWeekFromDate(weekStart)[0].date}
                    </Text>
                    <IconButton
                        icon={<ChevronRightIcon />}
                        onClick={() => changeWeek(1)}
                        aria-label="Next week"
                    />
                    <Button
                        leftIcon={<CopyIcon />}
                        variant="outline"
                        onClick={copyLastWeek}
                    >
                        Copy last week
                    </Button>
                </HStack>
            )}

            {loading && <Spinner mt={4} />}

            {courier && !loading && (
                <>
                    <Table mt={6}>
                        <Thead>
                            <Tr>
                                <Th>Date</Th>
                                <Th>Start Time</Th>
                                <Th>Route</Th>
                                <Th>Day Off</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {week.map((d, i) => (
                                <Tr key={d.date}>
                                    <Td>{d.date}</Td>
                                    <Td>
                                        <Input
                                            type="time"
                                            value={d.startTime}
                                            isDisabled={d.dayOff}
                                            onChange={e =>
                                                updateDay(i, 'startTime', e.target.value)
                                            }
                                        />
                                    </Td>
                                    <Td>
                                        <Select
                                            value={d.routeId}
                                            isDisabled={d.dayOff}
                                            onChange={e =>
                                                updateDay(i, 'routeId', e.target.value)
                                            }
                                        >
                                            <option value="">Select</option>
                                            {routes.map(r => (
                                                <option key={r._id} value={r._id}>
                                                    {r.name}
                                                </option>
                                            ))}
                                        </Select>
                                    </Td>
                                    <Td>
                                        <Checkbox
                                            isChecked={d.dayOff}
                                            onChange={e =>
                                                updateDay(i, 'dayOff', e.target.checked)
                                            }
                                        />
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>

                    <Button mt={6} colorScheme="blue" onClick={saveWeek}>
                        Save Week
                    </Button>
                </>
            )}
        </Box>
    );
};

export default CreateUpdateSchedulePage;
